// Two things that help a venue get Clavian Boot working without technical staff, in the console's words:
//
//   - `problems`: the Floor's "What needs you" cards for PCs that start from the boot server. Each is one plain sentence,
//     the PCs it is about, how long, and the one button that fixes it.
//   - `setup`: the guided Clavian Boot setup on the venue's Setup page — five steps, each ticked from what the venue has.
//
// Product names are used only where a venue has both products and the group would otherwise be ambiguous. A venue with
// only Clavian Boot is never told it has "a product".
export const bootGuideCopy = {
    // -- The Floor's problem cards ---------------------------------------------------------------------------
    problems: {
        title: 'Problems to fix',
        since: 'for {{duration}}',
        more: '+{{count}} more',
        sinceJustNow: 'just now',
        pcs: 'PCs this is about',
        noImage_one: 'This PC can’t start: its class {{class}} has no Windows image.',
        noImage_other: '{{count}} PCs can’t start: their class {{class}} has no Windows image.',
        noImageNoClass_one: 'This PC can’t start: no Windows image is ready for it.',
        noImageNoClass_other: '{{count}} PCs can’t start: no Windows image is ready for them.',
        chooseImage: 'Choose an image for {{class}}',
        chooseImageNoClass: 'Choose an image',
        licence_one: 'This PC can’t start: the Clavian Boot licence has run out.',
        licence_other: '{{count}} PCs can’t start: the Clavian Boot licence has run out.',
        noServer_one: 'This PC can’t start: no boot server is paired.',
        noServer_other: '{{count}} PCs can’t start: no boot server is paired.',
        serverOffline: 'The boot server has not been seen for {{minutes}} minutes. PCs already running keep running.',
        serverOfflineNever: 'The boot server has not been seen yet. PCs already running keep running.',
        openServer: 'Open the boot server',
        otherBoard_one: 'This PC can’t start: its motherboard is not the model its Windows image was made on.',
        otherBoard_other: '{{count}} PCs can’t start: their motherboard is not the one their Windows image was made on.',
        whatToDo: 'What to do',
        otherBoardTitle: 'A PC with a different motherboard',
        otherBoardIntro: 'A Windows image works on the kind of motherboard it was made on. This PC has a different one, so it may not start.',
        otherBoardMake: 'Make a new image on a PC with this motherboard.',
        otherBoardMove: 'Or move this PC to a class whose image was made on this motherboard.',
        otherBoardDone: 'Got it',
        startFailed_one: 'This PC stopped while it was starting. Restart it. If it stops again, check its network cable.',
        startFailed_other: '{{count}} PCs stopped while they were starting. Restart them. If they stop again, check their network cables.',
        openPc: 'Open the PC',
        notReaching: 'PCs are not reaching the boot server. Check it is on and on the same network.',
        letter_one: 'Games could not take {{letter}}: on this PC (another drive uses it).',
        letter_other: 'Games could not take {{letter}}: on {{count}} PCs (another drive uses it).',
        changeLetter: 'Change the letter',
        behind_one: '{{count}} PC is waiting for the latest Windows ({{image}} v{{version}}).',
        behind_other: '{{count}} PCs are waiting for the latest Windows ({{image}} v{{version}}).',
        restartAll: 'Restart all when free',
        diskLow: 'The boot server’s drive for {{role}} is nearly full ({{free}} left).',
    },
    // -- Guided Clavian Boot setup, on the venue's Setup page ---------------------------------------------------
    setup: {
        // The group: named after the product only where the venue has both.
        group: {
            titleBoth: 'Clavian Boot',
            titleOnly: 'Set up your PCs',
            hintBoth: 'Start your gaming PCs from one boot server. Suggested, and not needed to go live.',
            hintOnly: 'Get your PCs starting from one boot server. Each step ticks off by itself as it happens.',
            label: 'Boot setup steps',
            progress: '{{done}} of {{total}} done',
        },
        status: { done: 'Done', todo: 'To do', skipped: 'Skipped' },
        skip: 'Skip',
        skipNamed: 'Skip: {{step}}',
        unskip: 'Undo skip',
        unskipNamed: 'Undo skip: {{step}}',
        skippedHere: 'Skipped on this device',
        noRights: 'Someone who manages Clavian Boot does this step.',
        server: {
            title: 'Boot server paired',
            why: 'The boot server is one computer on your network that your PCs start from.',
            action: 'Set up the boot server',
            actionDone: 'See the boot server',
            stuckOffline_one: 'The boot server has not been seen for {{count}} minute.',
            stuckOffline_other: 'The boot server has not been seen for {{count}} minutes.',
            stuckNever: 'The boot server has not been seen yet.',
            stuckPairing: 'Waiting for the boot server to use its pairing code.',
            doneLine: '{{name}} is paired and online.',
        },
        serverDialog: {
            title: 'Set up the boot server',
            intro: 'Pick one computer on your network, such as a spare PC with plenty of disk space, to serve Windows to your PCs.',
            step1: 'On that computer, install Clavian Boot. Ask Clavian for the setup file if you do not have it yet.',
            step2: 'Get a pairing code here. It works once, for 30 minutes.',
            step3: 'On that computer, open the boot server’s status page, find the Pairing box and type the code in.',
            get: 'Get a pairing code',
            codeLabel: 'Pairing code',
            codeIntro: 'Type this code into the boot server.',
            expires: 'The code stops working at {{time}}.',
            failed: 'The pairing code could not be made. Try again.',
            done: 'Done',
            waiting: 'This page ticks the step off by itself once the boot server is online.',
        },
        networkStart: {
            title: 'A PC started from the network',
            why: 'Switch on one PC and start it from the network. That shows your network can carry it.',
            action: 'Test a network start',
            doneLine: 'A PC has started from the network.',
            intro: 'Do this once, with any PC that can start from the network.',
            step1: 'Switch on one PC.',
            step2: 'Open its boot menu and choose to start from the network (PXE). Most PCs show the menu key, often F12 or F11, as they start.',
            step3: 'Leave this page open. We tell you the moment it arrives.',
            start: 'Start the test',
            tryAgain: 'Try again',
            stop: 'Stop waiting',
            waiting: 'Waiting for a PC to start from the network…',
            seenTitle: 'We saw it',
            seen: 'A PC started from the network. Its network address is {{mac}}.',
            seenHardware: 'It reports {{hardware}}.',
            seenWhen: 'Seen {{when}}.',
            nothingTitle: 'Nothing arrived',
            nothing: 'Your router may be answering network starts itself, or the boot server’s network is set to Public.',
            switchTitle: 'Put a switch between them',
            switchBody: 'If your router answers PCs on its own, plug the boot server and the PCs into a separate switch so the boot server answers them instead.',
            diagramTitle: 'The boot server and the PCs share a switch',
            diagramDesc: 'A router connects to a switch. The boot server and the PCs are plugged into the switch.',
            diagramRouter: 'Router',
            diagramSwitch: 'Switch',
            diagramServer: 'Boot server',
            diagramPcs: 'PCs',
            publicTitle: 'Or set the boot server’s network to Private',
            public1: 'On the boot server, open Settings, then Network & internet.',
            public2: 'Open the network the boot server is connected to.',
            public3: 'Under Network profile, choose Private.',
            public4: 'Then try the test again.',
            needServer: 'Pair the boot server first.',
            offline: 'The boot server has not been seen for {{count}} minutes, so a test cannot work yet.',
        },
        image: {
            title: 'First Windows image',
            why: 'Capture Windows from a PC you have set up the way you want every PC to be. On that PC, download the capture app and run Trim Windows first, so the capture is small.',
            action: 'Capture Windows from a PC',
            actionProgress: 'See progress',
            actionDone: 'See the images',
            doneLine: '{{name}} is ready to start PCs.',
            inProgress: '{{name}} is being made',
            percent: '{{what}} · {{percent}}%',
            needServer: 'Pair the boot server first.',
        },
        games: {
            title: 'Games added',
            why: 'Put your games on one shared disk that every PC sees as a second drive. Skip this if players install their own games.',
            action: 'Add games from a folder',
            actionDone: 'See the games',
            doneLine: '{{name}} is ready.',
            needServer: 'Pair the boot server first.',
        },
        pcs: {
            title: 'PCs on the floor',
            why: 'A PC that starts from the network waits in New PCs on the Floor. Make it a station there to put it on the floor.',
            action: 'Open the Floor',
            doneLine_one: '{{count}} PC is on the floor.',
            doneLine_other: '{{count}} PCs are on the floor.',
            waiting_one: '{{count}} new PC is waiting on the Floor.',
            waiting_other: '{{count}} new PCs are waiting on the Floor.',
        },
    },
};
//# sourceMappingURL=boot-guide.js.map